import { ChangeEvent, Fragment } from "react";
import { useFormContext } from "react-hook-form";
import { AutoAwesome, Undo } from "@mui/icons-material";
import styled from "@emotion/styled";
import { TextField, Typography } from "@avenue-8/sidekick-ui";
import {
  ContactFieldItem,
  multiValueToItems,
  toSaferExternalHref,
} from "./contact-fields-helpers";
import { Button, Link, Tooltip, css } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  parseFacebookHandleInput,
  parseInstagramHandleInput,
  parseLinkedInHandleInput,
} from "./contact-summary-chips-helpers";

const renderCurrentValue = (field: ContactFieldItem) => {
  if (field.value.trim() === "") {
    return <EmptyValueText>None</EmptyValueText>;
  }
  if (field.inputType === "email") {
    const emails = multiValueToItems(field.value);
    return emails.map((email, i) => (
      <Fragment key={`email-${i}`}>
        <Link href={`mailto:${email}`}>{email}</Link>
        {i < emails.length - 1 && <span>, </span>}
      </Fragment>
    ));
  }
  if (field.inputType === "tel") {
    const phones = multiValueToItems(field.value);
    return phones.map((phone, i) => (
      <Fragment key={`phone-${i}`}>
        <Link href={`tel:${phone}`}>{phone}</Link>
        {i < phones.length - 1 && <span>, </span>}
      </Fragment>
    ));
  }
  if (field.inputType === "url") {
    const links = multiValueToItems(field.value);
    return links.map((link, i) => {
      const safeHref = toSaferExternalHref(link);
      return (
        <Fragment key={`link-${i}`}>
          {!!safeHref && (
            <Link href={safeHref} target="_blank" rel="noopener noreferrer">
              {safeHref}
            </Link>
          )}
          {!safeHref && <Typography>{link}</Typography>}
          {i < links.length - 1 && <span>, </span>}
        </Fragment>
      );
    });
  }
  return <Typography component="span">{field.value}</Typography>;
};

export const ContactFieldsFormField = (props: {
  field: ContactFieldItem;
  isEditing: boolean;
  onChange: (
    fieldId: string,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onDismissSuggestedValue: (fieldId: string) => void;
  onSeeSuggestedValue: (fieldId: string) => void;
}) => {
  const {
    field,
    isEditing,
    onChange,
    onDismissSuggestedValue,
    onSeeSuggestedValue,
  } = props;
  const form = useFormContext();
  const currentFormValue = form.watch(field.id);
  const fieldError = form.getFieldState(field.id, form.formState).error;
  const isCurrentFormValueSuggested =
    field.suggestedValue != null &&
    field.suggestedValue !== field.value &&
    currentFormValue === field.suggestedValue;
  const fieldLabel = (
    <SuggestionLabelSuffix $showSuffix={isCurrentFormValueSuggested}>
      <AutoAwesomeIcon fontSize="inherit" /> Suggested Value
    </SuggestionLabelSuffix>
  );

  const normalizeSocialHandleOnBlur = (rawValue: string) => {
    if (field.id === "instagramHandle") {
      const result = parseInstagramHandleInput(rawValue);
      if (result.ok) {
        form.setValue(field.id, result.handle, { shouldDirty: true });
      }
      return;
    }
    if (field.id === "linkedinHandle") {
      const result = parseLinkedInHandleInput(rawValue);
      if (result.ok) {
        form.setValue(field.id, result.handle, { shouldDirty: true });
      }
      return;
    }
    if (field.id === "facebookHandle") {
      const result = parseFacebookHandleInput(rawValue);
      if (result.ok) {
        form.setValue(field.id, result.handle, { shouldDirty: true });
      }
    }
  };

  return (
    <Fragment key={field.id}>
      <ContactFieldCell $isEditing={isEditing} $isLabel>
        <Typography fontWeight="bold">{field.label}</Typography>
      </ContactFieldCell>

      <ContactFieldCell $isEditing={isEditing}>
        <Typography>{renderCurrentValue(field)}</Typography>
      </ContactFieldCell>
      {isEditing && (
        <>
          <ContactFieldCell $isEditing={isEditing}>
            <Typography component="span">{"->"}</Typography>
          </ContactFieldCell>
          <ContactFieldCell $isEditing={isEditing}>
            <InlineEditField
              {...form.register(field.id, {
                onChange: (event) => onChange(field.id, event),
                onBlur: (event) =>
                  normalizeSocialHandleOnBlur(event.target.value),
              })}
              label={field.suggestedValue != null ? fieldLabel : undefined}
              variant="standard"
              fieldError={fieldError}
              hideHelperTextSpace
              fullWidth
            />
            {field.suggestedValue != null && (
              <>
                {isCurrentFormValueSuggested && (
                  <Tooltip title={"Dismiss"}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => onDismissSuggestedValue(field.id)}
                    >
                      <Undo fontSize="inherit" />
                    </Button>
                  </Tooltip>
                )}
                {!isCurrentFormValueSuggested && (
                  <Tooltip title={"See suggestion"}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => onSeeSuggestedValue(field.id)}
                    >
                      <AutoAwesome fontSize="inherit" />
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
          </ContactFieldCell>
        </>
      )}
    </Fragment>
  );
};

const ContactFieldCell = styled.div<{ $isEditing?: boolean; $isLabel?: boolean }>`
  height: 100%;
  border-top: 1px solid ${(p) => p.theme.colors.border2};
  padding: 0.5rem 0.25rem;
  align-self: center;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  overflow-wrap: anywhere;

  & > * {
    min-width: 0;
  }

  & > button {
    padding: 0.25rem 0.5rem;
    min-width: 2rem;
    height: 2rem;
  }

  &:nth-of-type(-n + ${(p) => (p.$isEditing ? "4" : "2")}) {
    border-top: none;
  }

  ${(p) => p.theme.media.mobileDown} {
    border-top: none;
    &:nth-of-type(2n + 1) {
      border-top: 1px solid ${(p) => p.theme.colors.border2};
    }
    ${(p) =>
      p.$isLabel &&
      css`
        padding-bottom: 0;
      `}
    ${(p) =>
      p.$isEditing &&
      css`
        &:nth-of-type(4n + 3) {
          display: none;
        }
      `}
  }
`;

const InlineEditField = styled(TextField)`
  .MuiFormLabel-root {
    min-height: 1rem;
    display: inline-flex;
    align-items: center;
  }

  .MuiInputBase-root {
    margin-top: 0;
    background: transparent;
    font-size: ${(p) => p.theme.text.fontSizes.m};
    align-items: center;
    width: 100%;
  }

  .MuiInputBase-input:focus {
    background: ${(p) => p.theme.colors.background2};
  }

  .MuiInputBase-input {
    // !important is required to override the default !important...
    padding: 0.125rem 0.5rem !important;
  }

  ${(p) => p.theme.media.mobileDown} {
    padding-bottom: 0.25rem;
  }
`;

const EmptyValueText = styled.span`
  font-style: italic;
  opacity: 0.6;
  color: ${(p) => p.theme.text.colors.secondary};
`;

const SuggestionLabelSuffix = styled.span<{ $showSuffix?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  opacity: ${(p) => (p.$showSuffix ? "1" : "0")};
  transition: opacity 0.5s ease-in-out;
`;
